import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useContext } from 'react';

import {
  CHATBV_GA4_EVENTS,
  DETECT_IMAGE_REPORTS,
  FUNCTION_CALL_OUTPUT,
  LOADING_STATUS,
  RESPONSE_TYPE,
  TRIGGER_SEARCH,
} from 'components/ChatBV/constants';
import { analyzeImageForReports } from 'components/ChatBV/services';
import {
  Message,
  LoadingStatus,
  GeneratedReport,
  GenericLLMRequest,
  GenericLLMRequestItem,
  FunctionCallResponse,
  PossibleReportParams,
} from 'components/ChatBV/types';
import {
  buildUserRequest,
  handleFunctionCalls,
  mapResponsesAPIResponseToProcessedResponse,
  isPdfFile,
} from 'components/ChatBV/utils';
import { ROLES } from 'components/SearchpalChatbot/utils/constants';

interface UseImageSearchParams {
  logError: (message: string, error: Error) => void;
  token: string | undefined;
  lastContextId: string | undefined;
  generatedReports: Record<string, GeneratedReport>;
  setUserOrientedMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>;
  handleConversation: (request: GenericLLMRequest) => Promise<any>;
  sendMessageToLLM: (request: GenericLLMRequest) => Promise<any>;
  handleReportsSelected?: (
    selectedReport: PossibleReportParams,
    skipIdleCheck?: boolean
  ) => Promise<void>;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };

    reader.onload = () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        if (!arrayBuffer) {
          reject(new Error('Invalid image data'));
          return;
        }

        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }

        const base64String = btoa(binary);

        if (!base64String || base64String.length === 0) {
          reject(new Error('Empty base64 image data'));
          return;
        }

        resolve(base64String);
      } catch (error) {
        reject(error);
      }
    };

    reader.readAsArrayBuffer(file);
  });
};

const extractSearchValue = (searchType: string, searchArgs: any): string => {
  let searchValue = '';
  if (searchType === 'phone' && searchArgs.phone_number) {
    let phoneNumber = searchArgs.phone_number.replace(/\D/g, '');
    if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.substring(1);
    }
    searchValue = phoneNumber;
  } else if (searchType === 'email' && searchArgs.email) {
    searchValue = searchArgs.email;
  } else if (searchType === 'vehicle') {
    if (searchArgs.vehicle_vin) {
      searchValue = searchArgs.vehicle_vin;
    } else if (searchArgs.vehicle_license_plate && searchArgs.state) {
      searchValue = `${searchArgs.vehicle_license_plate} ${searchArgs.state}`;
    } else if (
      searchArgs.vehicle_year &&
      searchArgs.vehicle_make &&
      searchArgs.vehicle_model
    ) {
      searchValue = `${searchArgs.vehicle_year} ${searchArgs.vehicle_make} ${searchArgs.vehicle_model}`;
      if (searchArgs.vehicle_trim) {
        searchValue += ` ${searchArgs.vehicle_trim}`;
      }
    }
  } else if (searchType === 'property') {
    if (searchArgs.address && searchArgs.city && searchArgs.state) {
      searchValue = `${searchArgs.address}, ${searchArgs.city}, ${searchArgs.state}`;
      if (searchArgs.zipcode) {
        searchValue += ` ${searchArgs.zipcode}`;
      }
    }
  }
  return searchValue;
};

const extractSearchValueFromReport = (report: PossibleReportParams): string => {
  const searchType = report.search_type;
  let searchValue = '';
  if (searchType === 'phone' && report.phone_number) {
    let phoneNumber = report.phone_number.replace(/\D/g, '');
    if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.substring(1);
    }
    searchValue = phoneNumber;
  } else if (searchType === 'email' && report.email) {
    searchValue = report.email;
  } else if (searchType === 'vehicle') {
    if (report.vehicle_vin) {
      searchValue = report.vehicle_vin;
    } else if (report.vehicle_license_plate && report.state) {
      searchValue = `${report.vehicle_license_plate} ${report.state}`;
    } else if (
      report.vehicle_year &&
      report.vehicle_make &&
      report.vehicle_model
    ) {
      searchValue = `${report.vehicle_year} ${report.vehicle_make} ${report.vehicle_model}`;
      if (report.vehicle_trim) {
        searchValue += ` ${report.vehicle_trim}`;
      }
    }
  } else if (searchType === 'property') {
    if (report.address && report.city && report.state) {
      searchValue = `${report.address}, ${report.city}, ${report.state}`;
      if (report.zipcode) {
        searchValue += ` ${report.zipcode}`;
      }
    }
  }
  return searchValue;
};

const createErrorMessage = (
  contextId: string,
  responseId: string,
  content: string
): Message => ({
  context_id: contextId,
  content,
  role: ROLES.assistant,
  response_id: responseId,
});

const handleNonPersonSearch = async (
  params: UseImageSearchParams,
  searchType: string,
  searchArgs: any,
  mappedResponse: any,
  lastContextId: string | undefined
): Promise<boolean> => {
  const searchValue = extractSearchValue(searchType, searchArgs);

  if (!searchValue) {
    const errorMessage = createErrorMessage(
      mappedResponse.context_id || lastContextId || '',
      mappedResponse.response_id,
      "We couldn't extract the search information from the image. Please try again."
    );
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }

  const textResponse = await params.sendMessageToLLM(
    buildUserRequest({ content: searchValue, role: ROLES.user }, undefined)
  );

  const [, functionCallsFromText] = textResponse.data;

  if (!functionCallsFromText || !Array.isArray(functionCallsFromText)) {
    const errorMessage = createErrorMessage(
      textResponse.context_id || lastContextId || '',
      textResponse.response_id,
      "We couldn't process the search. Please try again."
    );
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }

  const triggerSearchFromText = functionCallsFromText.find(
    (fc: FunctionCallResponse) => fc.name === TRIGGER_SEARCH
  );

  if (!triggerSearchFromText) {
    const errorMessage = createErrorMessage(
      textResponse.context_id || lastContextId || '',
      textResponse.response_id,
      "We couldn't process the search. Please try again."
    );
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }

  const textContextId = textResponse.context_id;
  const textCallId = triggerSearchFromText.call_id;

  if (!textContextId || !textCallId) {
    const errorMessage = createErrorMessage(
      textResponse.context_id || lastContextId || '',
      textResponse.response_id,
      "We couldn't get the required information from the search. Please try again."
    );
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }

  params.setLoadingStatus(LOADING_STATUS.ANALYZE);
  const processedFunctionCalls = await handleFunctionCalls(
    [triggerSearchFromText],
    params.generatedReports
  );
  params.setLoadingStatus(LOADING_STATUS.PREPARING);

  const processedCallResult =
    processedFunctionCalls.find((pfc) => pfc.call_id === textCallId) ||
    processedFunctionCalls[0];

  if (processedCallResult?.output) {
    try {
      const outputData = JSON.parse(processedCallResult.output);
      if (outputData?.error) {
        const errorMessage = createErrorMessage(
          textContextId || lastContextId || '',
          textResponse.response_id,
          outputData.error ||
            "We couldn't process the search. Please try again."
        );
        setTimeout(() => {
          params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
        }, 1500);
        setTimeout(() => {
          params.setLoadingStatus(LOADING_STATUS.IDLE);
        }, 1000);
        return true;
      }
    } catch {
      //
    }
  }

  if (processedCallResult?.permalink && processedCallResult?.reportData) {
    const functionCallOutput: GenericLLMRequestItem = {
      type: FUNCTION_CALL_OUTPUT,
      call_id: textCallId,
      output: processedCallResult.output,
      role: null,
      content: null,
      metadata: JSON.stringify({
        permalink: processedCallResult.permalink,
        searchType: processedCallResult.searchType,
      }),
    };

    const summaryRequest: GenericLLMRequest = {
      items: [functionCallOutput],
      context_id: textContextId,
    };

    const summaryResponse = await params.handleConversation(summaryRequest);

    const assistantMessage: Message = {
      context_id: summaryResponse.context_id || textContextId || '',
      content: summaryResponse.message,
      role: ROLES.assistant,
      response_id: summaryResponse.response_id,
      reports: [
        {
          reportData: processedCallResult.reportData,
          type: processedCallResult.searchType!,
          permalink: processedCallResult.permalink,
        },
      ],
    };
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }

  const errorMessage = createErrorMessage(
    textContextId || lastContextId || '',
    textResponse.response_id,
    "We couldn't find any results for the search. Please check the details and try again."
  );
  setTimeout(() => {
    params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
  }, 1500);
  setTimeout(() => {
    params.setLoadingStatus(LOADING_STATUS.IDLE);
  }, 1000);
  return true;
};

const handlePersonSearch = async (
  params: UseImageSearchParams,
  triggerSearchCall: FunctionCallResponse,
  mappedResponse: any
): Promise<boolean> => {
  params.setLoadingStatus(LOADING_STATUS.ANALYZE);
  const processedFunctionCalls = await handleFunctionCalls(
    [triggerSearchCall],
    params.generatedReports
  );
  params.setLoadingStatus(LOADING_STATUS.PREPARING);

  const originalCallId = triggerSearchCall.call_id;
  const originalContextId = mappedResponse.context_id;

  const processedCallResult =
    processedFunctionCalls.find((pfc) => pfc.call_id === originalCallId) ||
    processedFunctionCalls[0];

  if (processedCallResult?.permalink && processedCallResult?.reportData) {
    const metadata = {
      ...(processedCallResult.permalink && {
        permalink: processedCallResult.permalink,
      }),
      ...(processedCallResult.searchType && {
        searchType: processedCallResult.searchType,
      }),
      // Include params for person searches
      ...(processedCallResult.params && {
        params: processedCallResult.params,
      }),
      // Include teaser indicator for person searches (we'll restore teaser from output if needed)
      ...(processedCallResult.teaser &&
        processedCallResult.teaser.length > 0 && {
          hasTeaser: true,
        }),
    };

    const functionCallOutput: GenericLLMRequestItem = {
      type: FUNCTION_CALL_OUTPUT,
      call_id: originalCallId,
      output: processedCallResult.output,
      role: null,
      content: null,
      metadata: JSON.stringify(metadata),
    };

    const summaryRequest: GenericLLMRequest = {
      items: [functionCallOutput],
      context_id: originalContextId || null,
    };

    const summaryResponse = await params.handleConversation(summaryRequest);

    const assistantMessage: Message = {
      context_id: summaryResponse.context_id || originalContextId || '',
      content: summaryResponse.message,
      role: ROLES.assistant,
      response_id: summaryResponse.response_id,
      reports: [
        {
          reportData: processedCallResult.reportData,
          type: processedCallResult.searchType!,
          permalink: processedCallResult.permalink,
        },
      ],
    };
    setTimeout(() => {
      params.setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 1500);
    setTimeout(() => {
      params.setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
    return true;
  }
  return false;
};

const handleDetectImageCall = async (
  params: UseImageSearchParams,
  detectImageCall: FunctionCallResponse,
  mappedResponse: any,
  lastContextId: string | undefined
): Promise<boolean> => {
  const processedCalls = await handleFunctionCalls(
    [detectImageCall],
    params.generatedReports
  );
  const processedCall = processedCalls[0];

  if (processedCall?.possibleReports) {
    const possibleReports = processedCall.possibleReports;

    if (possibleReports.length > 0) {
      if (possibleReports.length === 1) {
        const singleReport = possibleReports[0];
        const searchValue = extractSearchValueFromReport(singleReport);

        if (!searchValue) {
          if (params.handleReportsSelected) {
            await params.handleReportsSelected(possibleReports[0], true);
          }
          return true;
        }

        const textResponse = await params.sendMessageToLLM(
          buildUserRequest(
            { content: searchValue, role: ROLES.user },
            undefined
          )
        );

        const [, functionCallsFromText] = textResponse.data;

        if (!functionCallsFromText || !Array.isArray(functionCallsFromText)) {
          if (params.handleReportsSelected) {
            await params.handleReportsSelected(possibleReports[0], true);
          }
          return true;
        }

        const triggerSearchFromText = functionCallsFromText.find(
          (fc: FunctionCallResponse) => fc.name === TRIGGER_SEARCH
        );

        if (!triggerSearchFromText) {
          if (params.handleReportsSelected) {
            await params.handleReportsSelected(possibleReports[0], true);
          }
          return true;
        }

        const textContextId = textResponse.context_id;
        const textCallId = triggerSearchFromText.call_id;

        if (!textContextId || !textCallId) {
          if (params.handleReportsSelected) {
            await params.handleReportsSelected(possibleReports[0], true);
          }
          return true;
        }

        params.setLoadingStatus(LOADING_STATUS.ANALYZE);
        const processedFunctionCalls = await handleFunctionCalls(
          [triggerSearchFromText],
          params.generatedReports
        );
        params.setLoadingStatus(LOADING_STATUS.PREPARING);

        const processedCallResult =
          processedFunctionCalls.find((pfc) => pfc.call_id === textCallId) ||
          processedFunctionCalls[0];

        if (processedCallResult?.output) {
          try {
            const outputData = JSON.parse(processedCallResult.output);
            if (outputData?.error) {
              const errorMessage = createErrorMessage(
                textContextId || '',
                textResponse.response_id,
                outputData.error ||
                  "We couldn't process the search. Please try again."
              );
              setTimeout(() => {
                params.setUserOrientedMessages((prev) => [
                  ...prev,
                  errorMessage,
                ]);
              }, 1500);
              setTimeout(() => {
                params.setLoadingStatus(LOADING_STATUS.IDLE);
              }, 1000);
              return true;
            }
          } catch {
            //
          }
        }

        if (processedCallResult?.permalink && processedCallResult?.reportData) {
          const functionCallOutput: GenericLLMRequestItem = {
            type: FUNCTION_CALL_OUTPUT,
            call_id: textCallId,
            output: processedCallResult.output,
            role: null,
            content: null,
            metadata: JSON.stringify({
              permalink: processedCallResult.permalink,
              searchType: processedCallResult.searchType,
            }),
          };

          const summaryRequest: GenericLLMRequest = {
            items: [functionCallOutput],
            context_id: textContextId,
          };

          const summaryResponse = await params.handleConversation(
            summaryRequest
          );

          const assistantMessage: Message = {
            context_id: summaryResponse.context_id || textContextId || '',
            content: summaryResponse.message,
            role: ROLES.assistant,
            response_id: summaryResponse.response_id,
            reports: [
              {
                reportData: processedCallResult.reportData,
                type: processedCallResult.searchType!,
                permalink: processedCallResult.permalink,
              },
            ],
          };
          setTimeout(() => {
            params.setUserOrientedMessages((prev) => [
              ...prev,
              assistantMessage,
            ]);
          }, 1500);
          setTimeout(() => {
            params.setLoadingStatus(LOADING_STATUS.IDLE);
          }, 1000);
          return true;
        }

        if (params.handleReportsSelected) {
          await params.handleReportsSelected(possibleReports[0], true);
        }
        return true;
      }

      const assistantMessage: Message = {
        context_id: mappedResponse.context_id || lastContextId || '',
        content: '',
        role: ROLES.assistant,
        response_id: mappedResponse.response_id,
        possibleReports,
      };
      setTimeout(() => {
        params.setUserOrientedMessages((prev) => [...prev, assistantMessage]);
      }, 1500);
      setTimeout(() => {
        params.setLoadingStatus(LOADING_STATUS.IDLE);
      }, 1000);
      return true;
    }
  }
  return false;
};

export const useImageSearch = (params: UseImageSearchParams) => {
  const { trackEventGA4 } = useContext(AppConfig);

  const processImageSearch = async (
    imageFile: File,
    lastContextId: string | undefined
  ): Promise<{ contextId: string | null; handled: boolean }> => {
    try {
      const base64Data = await fileToBase64(imageFile);

      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.photo_submitted.type,
      });

      const imageAnalysisResponse = await analyzeImageForReports(
        {
          base64Data,
          isPdf: isPdfFile(imageFile),
        },
        params.logError,
        params.token
      );

      let imageContextId: string | null = lastContextId || null;

      if (
        imageAnalysisResponse.type === RESPONSE_TYPE.RESPONSE &&
        imageAnalysisResponse.data
      ) {
        const mappedResponse = mapResponsesAPIResponseToProcessedResponse(
          imageAnalysisResponse.data
        );

        if (mappedResponse.context_id) {
          imageContextId = mappedResponse.context_id;
        }

        const [answer] = mappedResponse.data;
        if (
          answer &&
          typeof answer === 'string' &&
          (answer.includes('No report types detected') ||
            answer.includes('No reportable info'))
        ) {
          const assistantMessage: Message = {
            context_id: mappedResponse.context_id || lastContextId || '',
            content:
              "**We couldn't make sense of that file:** \n\nThe file couldn't be identified or read properly. Try uploading a clearer file or one that better shows what you're looking for.",
            role: ROLES.assistant,
            response_id: mappedResponse.response_id,
          };
          setTimeout(() => {
            params.setUserOrientedMessages((prev) => [
              ...prev,
              assistantMessage,
            ]);
          }, 1500);
          setTimeout(() => {
            params.setLoadingStatus(LOADING_STATUS.IDLE);
          }, 1000);
          return { contextId: imageContextId, handled: true };
        }

        const [, functionCalls] = mappedResponse.data;
        if (functionCalls && Array.isArray(functionCalls)) {
          const errorHandlerCall = functionCalls.find(
            (fc) => fc.name === 'error_handler' || fc.name === 'errorHandler'
          );

          if (errorHandlerCall) {
            const assistantMessage: Message = {
              context_id: mappedResponse.context_id || lastContextId || '',
              content:
                "We couldn't process the file. Please upload a clearer file.",
              role: ROLES.assistant,
              response_id: mappedResponse.response_id,
            };
            setTimeout(() => {
              params.setUserOrientedMessages((prev) => [
                ...prev,
                assistantMessage,
              ]);
            }, 1500);
            setTimeout(() => {
              params.setLoadingStatus(LOADING_STATUS.IDLE);
            }, 1000);
            return { contextId: imageContextId, handled: true };
          }

          const triggerSearchCall = functionCalls.find(
            (fc) => fc.name === TRIGGER_SEARCH
          );

          const detectImageCall = functionCalls.find(
            (fc) => fc.name === DETECT_IMAGE_REPORTS
          );

          if (triggerSearchCall) {
            const searchArgs = triggerSearchCall.arguments as any;
            const searchType = searchArgs?.search_type;

            if (
              searchType === 'phone' ||
              searchType === 'email' ||
              searchType === 'vehicle' ||
              searchType === 'property'
            ) {
              await handleNonPersonSearch(
                params,
                searchType,
                searchArgs,
                mappedResponse,
                lastContextId
              );
              return { contextId: imageContextId, handled: true };
            }

            if (searchType === 'person') {
              const handled = await handlePersonSearch(
                params,
                triggerSearchCall,
                mappedResponse
              );
              return { contextId: imageContextId, handled };
            }

            if (
              searchType === 'phone' ||
              searchType === 'email' ||
              searchType === 'vehicle' ||
              searchType === 'property'
            ) {
              const errorMessage = createErrorMessage(
                mappedResponse.context_id || lastContextId || '',
                mappedResponse.response_id,
                "We couldn't process the search. Please try again."
              );
              setTimeout(() => {
                params.setUserOrientedMessages((prev) => [
                  ...prev,
                  errorMessage,
                ]);
              }, 1500);
              setTimeout(() => {
                params.setLoadingStatus(LOADING_STATUS.IDLE);
              }, 1000);
              return { contextId: imageContextId, handled: true };
            }
          }

          if (detectImageCall) {
            const handled = await handleDetectImageCall(
              params,
              detectImageCall,
              mappedResponse,
              lastContextId
            );
            return { contextId: imageContextId, handled };
          }
        }
      }

      return { contextId: imageContextId, handled: false };
    } catch (_error) {
      const errorMessage: Message = {
        content: "We couldn't process the file. Please try again.",
        role: ROLES.assistant,
      };
      setTimeout(() => {
        params.setUserOrientedMessages((prev) => [...prev, errorMessage]);
      }, 1500);
      setTimeout(() => {
        params.setLoadingStatus(LOADING_STATUS.IDLE);
      }, 1000);
      return { contextId: null, handled: true };
    }
  };

  return { processImageSearch };
};
